import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { CreditCard, Package, CheckCircle, AlertCircle, Loader2, ExternalLink, Smartphone, User, Mail, Phone, MapPin, GraduationCap, Calendar, Clock, Pencil, Save, X, Upload, FileText, Hash, Building, Eye, Plus, Trash2, ShieldAlert, Ban, Search, RefreshCw, Square, BookOpen, Award, MessageSquare, Download, ChevronDown, ChevronUp, History, Users } from "lucide-react";
import ModalPortal from "@/components/ModalPortal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatKr(amount) {
  if (amount == null) return "—";
  return new Intl.NumberFormat("da-DK", { style: "currency", currency: "DKK", maximumFractionDigits: 0 }).format(amount);
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("da-DK", { day: "numeric", month: "short", year: "numeric" });
  } catch { return dateStr; }
}

const STATUS_STYLES = {
  "Aktiv":                { bg: "bg-green-500/10",  text: "text-green-600 dark:text-green-400",  dot: "bg-green-500" },
  "Tilmeldt Uddannelse":  { bg: "bg-green-500/10",  text: "text-green-600 dark:text-green-400",  dot: "bg-green-500" },
  "Orlov":                { bg: "bg-purple-500/10", text: "text-purple-600 dark:text-purple-400", dot: "bg-purple-500" },
  "Pause":                { bg: "bg-purple-500/10", text: "text-purple-600 dark:text-purple-400", dot: "bg-purple-500" },
  "Stoppet før tid":      { bg: "bg-red-500/10",    text: "text-red-600 dark:text-red-400",       dot: "bg-red-500" },
  "Stoppet":              { bg: "bg-red-500/10",    text: "text-red-600 dark:text-red-400",       dot: "bg-red-500" },
  "Starter senere":       { bg: "bg-blue-500/10",   text: "text-blue-600 dark:text-blue-400",     dot: "bg-blue-500" },
  "Afsluttet uddannelse": { bg: "bg-yellow-500/10", text: "text-yellow-600 dark:text-yellow-400", dot: "bg-yellow-500" },
  "Afsluttet":            { bg: "bg-yellow-500/10", text: "text-yellow-600 dark:text-yellow-400", dot: "bg-yellow-500" },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status];
  if (!status) return <span className="text-sm text-muted-foreground">Ikke angivet</span>;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[13px] font-semibold ${s ? s.bg : "bg-secondary"} ${s ? s.text : "text-muted-foreground"}`}>
      {s && <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />}
      {status}
    </span>
  );
}

function InfoRow({ label, value, highlight }) {
  return (
    <div className="flex justify-between items-center py-2.5 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={`text-sm font-semibold ${highlight ? "text-[#FF2A6D]" : "text-foreground"}`}>{value}</span>
    </div>
  );
}

function EditableField({ icon: Icon, label, value, field, editing, draft, onChange, type, multiline }) {
  if (!editing) {
    if (!value) return null;
    return (
      <div className="flex items-start gap-2 py-2.5 border-b border-border last:border-0">
        <span className="text-sm text-muted-foreground flex items-center gap-1.5 shrink-0 min-w-[100px]"><Icon className="w-3.5 h-3.5" />{label}</span>
        <span className="text-sm font-semibold text-foreground text-right flex-1 break-words">{value}</span>
      </div>
    );
  }
  return (
    <div className="flex justify-between items-center py-2 border-b border-border last:border-0 gap-3">
      <span className="text-sm text-muted-foreground flex items-center gap-1.5 shrink-0"><Icon className="w-3.5 h-3.5" />{label}</span>
      <input
        type={type || "text"}
        value={draft[field] ?? value ?? ""}
        onChange={(e) => onChange(field, e.target.value)}
        className="text-sm font-semibold text-foreground bg-secondary border border-border rounded-lg px-2.5 py-1.5 text-right w-full max-w-[220px] focus:outline-none focus:ring-1 focus:ring-[#FF2A6D] transition-colors"
      />
    </div>
  );
}

/* ─── Fold-ud boks ─── */
function CollapsibleCard({ title, icon: Icon, iconBg, iconColor, children, defaultOpen = false, badge }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-secondary/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-full ${iconBg} flex items-center justify-center`}>
            <Icon className={`w-4 h-4 ${iconColor}`} />
          </div>
          <span className="text-sm font-bold text-foreground uppercase tracking-wider">{title}</span>
          {badge != null && (
            <span className="text-[13px] text-muted-foreground bg-secondary px-2 py-0.5 rounded-full">{badge}</span>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>
      {open && <div className="px-5 pb-5">{children}</div>}
    </div>
  );
}

/* ─── Confirmation dialog ─── */
function ConfirmDialog({ open, title, body, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-sm p-6">
        <h3 className="text-base font-bold text-foreground mb-2">{title}</h3>
        {body && <p className="text-sm text-muted-foreground mb-5">{body}</p>}
        <div className="flex items-center gap-2 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-full text-[13px] font-semibold text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
          >
            Fortryd
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 rounded-full text-[13px] font-semibold bg-[#FF2A6D] text-white hover:bg-[#e02460] transition-colors"
          >
            Gem ændringer
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Stamdata log fold-out ─── */
const ACTION_LABELS = {
  stamdata_opdateret: "Stamdata opdateret",
  karakter_opdateret: "Karakter opdateret",
  klar_til_eksamen: "Klar til eksamen markeret",
  oprettet: "Oprettet",
  slettet: "Slettet",
  billede_uploadet: "Billede uploadet",
  kontrakt_uploadet: "Kontrakt uploadet",
};

function fmtLogTs(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString("da-DK", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return ts; }
}

function StamdataLogCard({ appItemId, adminToken }) {
  const [open, setOpen] = useState(false);
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterAction, setFilterAction] = useState("");

  const load = async () => {
    if (log !== null || loading) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/elev/stamdata/${appItemId}/log`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setLog(res.data.log || []);
    } catch { setLog([]); }
    finally { setLoading(false); }
  };

  const toggle = () => {
    if (!open) load();
    setOpen(o => !o);
  };

  const allActions = log ? [...new Set(log.map(e => e.action).filter(Boolean))] : [];

  const filtered = (log || []).filter(entry => {
    if (filterAction && entry.action !== filterAction) return false;
    if (search) {
      const q = search.toLowerCase();
      const inBy = (entry.changed_by || "").toLowerCase().includes(q);
      const inDiffs = (entry.diffs || []).some(d =>
        (d.felt || "").toLowerCase().includes(q) ||
        String(d.fra || "").toLowerCase().includes(q) ||
        String(d.til || "").toLowerCase().includes(q)
      );
      if (!inBy && !inDiffs) return false;
    }
    return true;
  });

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden mt-4">
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-secondary/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
            <History className="w-4 h-4 text-muted-foreground" />
          </div>
          <span className="text-sm font-bold text-foreground uppercase tracking-wider">Ændringslog</span>
          {log && log.length > 0 && (
            <span className="text-[11px] font-semibold bg-secondary text-muted-foreground rounded-full px-2 py-0.5">{log.length}</span>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>
      {open && (
        <div className="px-5 pb-5">
          {loading && <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>}
          {!loading && log && log.length === 0 && (
            <p className="text-[13px] text-muted-foreground text-center py-4">Ingen ændringer registreret</p>
          )}
          {!loading && log && log.length > 0 && (
            <>
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Søg..."
                  className="flex-1 h-8 px-3 rounded-lg border border-border bg-background text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
                {allActions.length > 1 && (
                  <select
                    value={filterAction}
                    onChange={e => setFilterAction(e.target.value)}
                    className="h-8 px-2 rounded-lg border border-border bg-background text-[13px] text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="">Alle handlinger</option>
                    {allActions.map(a => (
                      <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>
                    ))}
                  </select>
                )}
              </div>
              {filtered.length === 0 && (
                <p className="text-[13px] text-muted-foreground text-center py-2">Ingen resultater</p>
              )}
              <div className="space-y-3">
                {filtered.map((entry, i) => (
                  <div key={i} className="border-b border-border pb-3 last:border-0 last:pb-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[13px] font-semibold text-foreground">{entry.changed_by || "—"}</span>
                        <span className="text-[11px] font-medium bg-secondary text-muted-foreground rounded-full px-2 py-0.5">
                          {ACTION_LABELS[entry.action] || entry.action || "Ændring"}
                        </span>
                      </div>
                      <span className="text-[12px] text-muted-foreground whitespace-nowrap shrink-0">{fmtLogTs(entry.timestamp)}</span>
                    </div>
                    {(entry.diffs || []).length > 0 && (
                      <div className="space-y-0.5 mt-1">
                        {entry.diffs.map((d, j) => (
                          <div key={j} className="text-[13px] text-muted-foreground">
                            <span className="font-medium text-foreground">{d.felt}:</span>{" "}
                            <span className="line-through opacity-60">{d.fra != null ? String(d.fra) : "—"}</span>
                            {" → "}
                            <span className="text-foreground">{d.til != null ? String(d.til) : "—"}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Student photo + profile (left column on desktop) ─── */
function ProfilCard({ profil, isAdmin, isSuperAdmin = isAdmin, adminToken, appItemId, onUpdate }) {
  // Privacy: kun superadmin må se kontrakter og redigere CPR i klartekst.
  // Resten af admin/medarbejder-flowet ser CPR maskeret som "XXXXXX-XXXX" (kommer fra backend).
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [uploadingKontrakt, setUploadingKontrakt] = useState(false);
  const [kontraktChanges, setKontraktChanges] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoKey, setPhotoKey] = useState(0);
  const fileRef = useRef(null);
  const photoFileRef = useRef(null);

  if (!profil) return null;

  const handleEdit = () => {
    const base = {
      navn: profil.navn || "",
      email: profil.email || "",
      telefon: profil.telefon || "",
      adresse: profil.adresse || "",
      elev_id: profil.elev_id || "",
      afdeling: profil.afdeling || "",
    };
    // CPR kan kun redigeres af superadmin
    if (isSuperAdmin) base.cpr = profil.cpr || "";
    setDraft(base);
    setEditing(true);
  };

  const handleCancel = () => {
    setDraft({});
    setEditing(false);
  };

  const handleChange = (field, value) => {
    setDraft(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    setConfirmOpen(true);
  };

  const handleConfirmSave = async () => {
    setConfirmOpen(false);
    if (!adminToken || !appItemId) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/elev/stamdata/${appItemId}`, draft, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      onUpdate && onUpdate(draft);
      setEditing(false);
      setDraft({});
    } catch (err) {
      alert("Kunne ikke gemme: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleKontraktUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !adminToken || !appItemId) return;
    setUploadingKontrakt(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/admin/elev/stamdata/${appItemId}/kontrakt`, formData, {
        headers: { Authorization: `Bearer ${adminToken}`, "Content-Type": "multipart/form-data" },
      });
      onUpdate && onUpdate({ kontrakt_filnavn: file.name });
      setKontraktChanges("prompt");
    } catch (err) {
      alert("Kunne ikke uploade: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploadingKontrakt(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleKontraktDownload = async () => {
    if (!adminToken || !appItemId) return;
    try {
      const res = await fetch(`${API}/admin/elev/stamdata/${appItemId}/kontrakt`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (!res.ok) throw new Error("Download fejl");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (err) {
      alert("Kunne ikke hente kontrakt: " + err.message);
    }
  };

  const photoUrl = appItemId ? `${API}/elev/billede/${appItemId}?v=${photoKey}` : null;

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !adminToken || !appItemId) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/admin/elev/stamdata/${appItemId}/billede-upload`, formData, {
        headers: { Authorization: `Bearer ${adminToken}`, "Content-Type": "multipart/form-data" },
      });
      setPhotoKey(k => k + 1);
    } catch (err) {
      alert("Kunne ikke uploade billede: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploadingPhoto(false);
      if (photoFileRef.current) photoFileRef.current.value = "";
    }
  };

  return (
    <>
    <ConfirmDialog
      open={confirmOpen}
      title="Gem ændringer?"
      body="Er du sikker på at du vil gemme disse ændringer til elevens stamdata?"
      onConfirm={handleConfirmSave}
      onCancel={() => setConfirmOpen(false)}
    />
    <div className="bg-card border border-border rounded-2xl p-5">
      {/* Photo */}
      <div className="flex justify-center mb-4">
        <div className="relative group">
          <img
            src={photoUrl}
            alt={profil.navn || "Elev"}
            className="w-28 h-28 rounded-full object-cover border-2 border-border bg-secondary"
            onError={(e) => { e.target.style.display = "none"; e.target.nextSibling && (e.target.nextSibling.style.display = "flex"); }}
          />
          <div className="w-28 h-28 rounded-full border-2 border-border bg-secondary items-center justify-center hidden">
            <User className="w-10 h-10 text-muted-foreground" />
          </div>
          {isAdmin && (
            <>
              <button
                onClick={() => photoFileRef.current?.click()}
                className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
              >
                {uploadingPhoto ? <Loader2 className="w-5 h-5 animate-spin text-white" /> : <Upload className="w-5 h-5 text-white" />}
              </button>
              <input ref={photoFileRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
            </>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#FF2A6D]/10 flex items-center justify-center">
            <User className="w-4 h-4 text-[#FF2A6D]" />
          </div>
          <h3 className="text-sm font-bold text-foreground uppercase tracking-wider">Profil</h3>
        </div>
        {isAdmin && appItemId && !editing && (
          <button
            onClick={handleEdit}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full text-[13px] font-semibold text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
          >
            <Pencil className="w-3 h-3" />
            Rediger
          </button>
        )}
      </div>

      <div>
        <EditableField icon={Hash} label="Elev-nr." value={profil.elev_id} field="elev_id" editing={editing} draft={draft} onChange={handleChange} />
        <EditableField icon={User} label="Navn" value={profil.navn} field="navn" editing={editing} draft={draft} onChange={handleChange} />
        {/* CPR: kun superadmin må se i klartekst + redigere.
            Backend leverer "XXXXXX-XXXX" til non-superadmin, så vi viser feltet som read-only. */}
        {isSuperAdmin ? (
          <EditableField icon={User} label="CPR" value={profil.cpr} field="cpr" editing={editing} draft={draft} onChange={handleChange} />
        ) : (
          <div className="flex justify-between items-center py-2 border-b border-border last:border-0 gap-3">
            <span className="text-sm text-muted-foreground flex items-center gap-1.5 shrink-0"><User className="w-3.5 h-3.5" />CPR</span>
            <span className="text-sm text-muted-foreground font-mono">{profil.cpr || "—"}</span>
          </div>
        )}
        <EditableField icon={Mail} label="E-mail" value={profil.email} field="email" editing={editing} draft={draft} onChange={handleChange} />
        <EditableField icon={Phone} label="Telefon" value={profil.telefon} field="telefon" editing={editing} draft={draft} onChange={handleChange} />
        <EditableField icon={MapPin} label="Adresse" value={profil.adresse} field="adresse" editing={editing} draft={draft} onChange={handleChange} />
        <EditableField icon={Building} label="Skole" value={profil.afdeling} field="afdeling" editing={editing} draft={draft} onChange={handleChange} />
        {isAdmin && (
          <div className="flex justify-between items-center py-2 border-b border-border last:border-0 gap-3">
            <span className="text-sm text-muted-foreground flex items-center gap-1.5 shrink-0"><CheckCircle className="w-3.5 h-3.5" />Status</span>
            <div className="flex items-center gap-2">
              {(() => {
                const s = STATUS_STYLES[profil.elev_status];
                return (
                  <select
                    value={profil.elev_status || ""}
                    onChange={async (e) => {
                      const val = e.target.value;
                      onUpdate && onUpdate({ elev_status: val });
                      try {
                        await axios.put(`${API}/admin/elev/stamdata/${appItemId}`, { elev_status: val }, {
                          headers: { Authorization: `Bearer ${adminToken}` },
                        });
                      } catch { /* ignore */ }
                    }}
                    className={`appearance-none text-[13px] font-semibold rounded-full px-2.5 py-1 border-0 focus:outline-none cursor-pointer ${s ? `${s.bg} ${s.text}` : "bg-secondary text-muted-foreground"}`}
                  >
                <option value="">Ikke angivet</option>
                <option value="Tilmeldt Uddannelse">Tilmeldt Uddannelse</option>
                <option value="Aktiv">Aktiv</option>
                <option value="Starter senere">Starter senere</option>
                <option value="Orlov">Orlov</option>
                <option value="Afsluttet uddannelse">Afsluttet uddannelse</option>
                <option value="Stoppet før tid">Stoppet før tid</option>
                  </select>
                );
              })()}
            </div>
          </div>
        )}
      </div>

      {editing && (
        <div className="flex items-center gap-2 mt-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold bg-[#FF2A6D] text-white hover:bg-[#e02460] transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Gem
          </button>
          <button
            onClick={handleCancel}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
          >
            <X className="w-3 h-3" />
            Fortryd
          </button>
        </div>
      )}

      {/* Kontrakt section — KUN superadmin (kontrakter er fortrolige) */}
      {isSuperAdmin && appItemId && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Kontrakt</span>
            </div>
            <div className="flex items-center gap-2">
              {profil.kontrakt_filnavn && (
                <button
                  onClick={handleKontraktDownload}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-semibold bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 transition-colors"
                >
                  <Eye className="w-3 h-3" />
                  Åbn kontrakt
                </button>
              )}
              <label className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-semibold bg-secondary text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-colors cursor-pointer">
                {uploadingKontrakt ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                {profil.kontrakt_filnavn ? "Erstat" : "Upload"}
                <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={handleKontraktUpload} />
              </label>
            </div>
          </div>

          {kontraktChanges === "prompt" && (
            <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
              <p className="text-[13px] text-amber-600 dark:text-amber-400 font-semibold mb-2">
                Kontrakten er uploadet. Indeholder den ændringer til elevens data?
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => { setKontraktChanges(null); handleEdit(); }}
                  className="px-3 py-1.5 rounded-full text-[13px] font-semibold bg-amber-500 text-white hover:bg-amber-600 transition-colors"
                >
                  Ja, ret data
                </button>
                <button
                  onClick={() => setKontraktChanges(null)}
                  className="px-3 py-1.5 rounded-full text-[13px] font-semibold text-muted-foreground hover:bg-secondary transition-colors"
                >
                  Nej, data er korrekt
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
    {isAdmin && appItemId && (
      <StamdataLogCard appItemId={appItemId} adminToken={adminToken} />
    )}
    </>
  );
}

/* ─── Andet card (rekruttering + rundvisning) ─── */
function AndetCard({ rekruttering, rundvisning, isAdmin, adminToken, appItemId, onUpdate }) {
  const [editingRekr, setEditingRekr] = useState(false);
  const [rekrDraft, setRekrDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [undervisere, setUndervisere] = useState([]);

  const REKR_OPTIONS = [
    "Studentum", "Facebook", "Instagram", "Google", "Website",
    "Gennem venner", "Jobindex", "Opslag i butik", "Uddannelsesmesse", "Andet"
  ];

  useEffect(() => {
    if (!isAdmin || !adminToken) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/undervisere`, {
          headers: { Authorization: `Bearer ${adminToken}` },
        });
        setUndervisere(res.data.undervisere || []);
      } catch { /* ignore */ }
    })();
  }, [isAdmin, adminToken]);

  const saveField = async (fields) => {
    if (!adminToken || !appItemId) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/elev/stamdata/${appItemId}`, fields, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      onUpdate && onUpdate(fields);
    } catch (err) {
      alert("Kunne ikke gemme: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const rv = rundvisning || {};
  const inputClass = "appearance-none bg-background border border-border rounded-lg px-3 h-9 text-sm text-foreground w-full focus:outline-none focus:ring-1 focus:ring-[#FF2A6D] disabled:opacity-60 transition-colors";

  return (
    <CollapsibleCard title="Andet" icon={History} iconBg="bg-green-500/10" iconColor="text-green-500">
      <div className="space-y-4">
        {/* Rekruttering */}
        <div>
          <p className="text-[13px] text-muted-foreground uppercase tracking-wider mb-1.5">Rekruttering</p>
          {editingRekr ? (
            <div>
              <select
                value={rekrDraft}
                onChange={(e) => setRekrDraft(e.target.value)}
                className={inputClass}
              >
                <option value="">Vælg kilde...</option>
                {REKR_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
              <div className="flex items-center gap-2 mt-2">
                <button
                  onClick={() => { saveField({ rekruttering: rekrDraft }); setEditingRekr(false); }}
                  disabled={saving || !rekrDraft}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-colors disabled:opacity-50"
                >
                  <Save className="w-3 h-3" /> Gem
                </button>
                <button onClick={() => setEditingRekr(false)} className="text-[13px] text-muted-foreground hover:text-foreground">Fortryd</button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-foreground">
                {rekruttering || <span className="text-muted-foreground font-normal">Ikke angivet</span>}
              </span>
              {isAdmin && (
                <button onClick={() => { setRekrDraft(rekruttering || ""); setEditingRekr(true); }} className="text-muted-foreground hover:text-foreground">
                  <Pencil className="w-3 h-3" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Rundvisning */}
        <div className="border-t border-border pt-4">
          <p className="text-[13px] text-muted-foreground uppercase tracking-wider mb-2">Rundvisning</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[13px] text-muted-foreground block mb-1">Dato</label>
              <input
                type="date"
                value={rv.dato || ""}
                onChange={e => {
                  const val = e.target.value;
                  onUpdate && onUpdate({ rundvisning: { ...rv, dato: val } });
                  saveField({ rundvisning: { ...rv, dato: val } });
                }}
                disabled={!isAdmin}
                className={inputClass}
              />
            </div>
            <div>
              <label className="text-[13px] text-muted-foreground block mb-1">Udført af</label>
              <select
                value={rv.person || ""}
                onChange={e => {
                  const val = e.target.value;
                  onUpdate && onUpdate({ rundvisning: { ...rv, person: val } });
                  saveField({ rundvisning: { ...rv, person: val } });
                }}
                disabled={!isAdmin}
                className={inputClass}
              >
                <option value="">Vælg person...</option>
                {undervisere.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>
      {saving && <div className="flex justify-end mt-2"><Loader2 className="w-3 h-3 animate-spin text-muted-foreground" /></div>}
    </CollapsibleCard>
  );
}

/* ─── Fritagelser card ─── */
function FritagelserCard({ ydelser, kundetyper }) {
  const hasYdelser = ydelser && ydelser.length > 0;
  const hasKundetyper = kundetyper && kundetyper.length > 0;

  if (!hasYdelser && !hasKundetyper) return null;

  return (
    <div className="bg-card border border-border rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-full bg-orange-500/10 flex items-center justify-center">
          <Ban className="w-4 h-4 text-orange-500" />
        </div>
        <h3 className="text-sm font-bold text-foreground uppercase tracking-wider">Fritagelser</h3>
      </div>

      {hasYdelser && (
        <div className="mb-3">
          <p className="text-[13px] text-muted-foreground uppercase tracking-wider mb-2">Fritagede ydelser</p>
          <div className="flex flex-wrap gap-1.5">
            {ydelser.map((y, i) => (
              <span key={i} className="px-2.5 py-1 rounded-full text-[13px] font-medium bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20">
                {y}
              </span>
            ))}
          </div>
        </div>
      )}

      {hasKundetyper && (
        <div>
          <p className="text-[13px] text-muted-foreground uppercase tracking-wider mb-2">Fritagede kundetyper</p>
          <div className="flex flex-wrap gap-1.5">
            {kundetyper.map((k, i) => (
              <span key={i} className="px-2.5 py-1 rounded-full text-[13px] font-medium bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20">
                {k}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Advarsler / Andet data card ─── */
function AdvarslerCard({ advarsler, isAdmin, adminToken, appItemId, onUpdate }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ overskrift: "", emne: "", dato: new Date().toISOString().split("T")[0] });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const handleAdd = async () => {
    if (!adminToken || !appItemId || !form.overskrift) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/elev/stamdata/${appItemId}/advarsel`, form, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      onUpdate && onUpdate(res.data.advarsel);
      setForm({ overskrift: "", emne: "", dato: new Date().toISOString().split("T")[0] });
      setShowForm(false);
    } catch (err) {
      alert("Kunne ikke oprette: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!adminToken || !appItemId) return;
    setDeleting(id);
    try {
      await axios.delete(`${API}/admin/elev/stamdata/${appItemId}/advarsel/${id}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      onUpdate && onUpdate(null, id);
    } catch (err) {
      alert("Kunne ikke slette: " + (err.response?.data?.detail || err.message));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center">
            <ShieldAlert className="w-4 h-4 text-red-500" />
          </div>
          <h3 className="text-sm font-bold text-foreground uppercase tracking-wider">Advarsler & noter</h3>
        </div>
        {isAdmin && appItemId && !showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full text-[13px] font-semibold text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
          >
            <Plus className="w-3 h-3" />
            Tilføj
          </button>
        )}
      </div>

      {/* Existing warnings */}
      {advarsler && advarsler.length > 0 ? (
        <div className="space-y-2 mb-4">
          {advarsler.map((a) => (
            <div key={a.id} className="p-3 rounded-xl border border-red-500/20 bg-red-500/5">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground">{a.overskrift}</p>
                  {a.emne && <p className="text-[13px] text-muted-foreground mt-1">{a.emne}</p>}
                  <p className="text-[13px] text-muted-foreground mt-1.5">
                    {formatDate(a.dato)}
                    {a.oprettet_af && <> · {a.oprettet_af}</>}
                  </p>
                </div>
                {isAdmin && (
                  <button
                    onClick={() => handleDelete(a.id)}
                    disabled={deleting === a.id}
                    className="shrink-0 p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors"
                  >
                    {deleting === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground mb-4">Ingen advarsler registreret</p>
      )}

      {/* Add form */}
      {showForm && (
        <div className="p-4 rounded-xl border border-border bg-secondary/50 space-y-3">
          <div>
            <label className="text-[13px] text-muted-foreground block mb-1">Overskrift *</label>
            <input
              type="text"
              value={form.overskrift}
              onChange={(e) => setForm(prev => ({ ...prev, overskrift: e.target.value }))}
              placeholder="F.eks. Skriftlig advarsel"
              className="w-full h-9 px-3 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-[#FF2A6D] transition-colors"
            />
          </div>
          <div>
            <label className="text-[13px] text-muted-foreground block mb-1">Emne / beskrivelse</label>
            <textarea
              value={form.emne}
              onChange={(e) => setForm(prev => ({ ...prev, emne: e.target.value }))}
              placeholder="Beskriv årsagen..."
              rows={2}
              className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-[#FF2A6D] transition-colors resize-none"
            />
          </div>
          <div>
            <label className="text-[13px] text-muted-foreground block mb-1">Dato</label>
            <input
              type="date"
              value={form.dato}
              onChange={(e) => setForm(prev => ({ ...prev, dato: e.target.value }))}
              className="h-9 px-3 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-[#FF2A6D] transition-colors"
            />
          </div>
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={handleAdd}
              disabled={saving || !form.overskrift}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold bg-[#FF2A6D] text-white hover:bg-[#e02460] transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
              Opret advarsel
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            >
              <X className="w-3 h-3" />
              Fortryd
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function formatExamMonth(ym) {
  if (!ym) return "—";
  const [y, m] = ym.split("-").map(Number);
  const months = ["jan.", "feb.", "mar.", "apr.", "maj", "jun.", "jul.", "aug.", "sep.", "okt.", "nov.", "dec."];
  return `${months[m - 1]} ${y}`;
}

// Dansk tal-format med 2 decimaler: 26,00 / 578,25
function formatTimer(n) {
  if (n == null) return "—";
  return Number(n).toLocaleString("da-DK", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// #46.6: ugedage til "Fra"-dropdown (mandag..fredag).
const UGE_DAGE = [
  { val: "man", label: "Mandag" },
  { val: "tir", label: "Tirsdag" },
  { val: "ons", label: "Onsdag" },
  { val: "tor", label: "Torsdag" },
  { val: "fre", label: "Fredag" },
];

// DD.MM af en ISO-dato (YYYY-MM-DD) — bruges i uge-dialogens overskrift.
function ddmm(isoDate) {
  if (!isoDate) return "";
  const [, m, d] = isoDate.split("-");
  return `${d}.${m}`;
}

// #46.6: Dialog til at markere en uge som Ferie eller Dobbelttimer. Vises via
// ModalPortal (ankret til viewporten, jf. #46.2). Brugeren vælger om markeringen
// gælder hele ugen eller et antal dage fra en valgt ugedag, og om det er ferie
// (0 / færre timer) eller dobbelttimer (dobbelt / flere timer). Gem → set_ferie/
// set_dobbelt; Ryd → clear_label (normal uge); Annullér → luk.
function UgeDialog({ row, busy, onSave, onClear, onClose }) {
  const [hele, setHele] = useState(true);        // true = hele ugen, false = antal dage
  const [antal, setAntal] = useState(3);          // 1-5 dage (når !hele)
  const [fra, setFra] = useState("man");          // man..fre
  const [mark, setMark] = useState("ferie");      // "ferie" | "dobbelt"

  // Forudfyld ud fra evt. eksisterende markering på rækken.
  useEffect(() => {
    if (row?.mark === "ferie" || row?.mark === "dobbelt") setMark(row.mark);
  }, [row]);

  const heading = `Uge ${row.uge} · ${ddmm(row.week_start)} - ${ddmm(row.week_end)}`;
  const dage = hele ? "hele" : antal;

  return (
    <ModalPortal onClose={onClose}>
      <div className="w-full sm:w-[420px] bg-card border border-border rounded-t-2xl sm:rounded-2xl p-5 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-foreground">{heading}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" disabled={busy}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Vælg dage */}
        <div className="mb-4">
          <p className="text-[13px] font-semibold text-foreground mb-2">Vælg dage</p>
          <label className="flex items-center gap-2 text-[13px] text-foreground cursor-pointer mb-2">
            <input type="radio" name="ugedage" checked={hele} onChange={() => setHele(true)} disabled={busy} />
            Hele ugen (mandag-søndag)
          </label>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-2 text-[13px] text-foreground cursor-pointer">
              <input type="radio" name="ugedage" checked={!hele} onChange={() => setHele(false)} disabled={busy} />
              Antal dage:
            </label>
            <input
              type="number" min="1" max="5" value={antal}
              onChange={(e) => setAntal(Math.max(1, Math.min(5, Number(e.target.value) || 1)))}
              onFocus={() => setHele(false)}
              disabled={busy}
              className="w-16 text-sm font-semibold text-foreground bg-secondary border border-border rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#FF2A6D] disabled:opacity-50"
            />
            <span className="text-[13px] text-muted-foreground">Fra:</span>
            <select
              value={fra}
              onChange={(e) => { setFra(e.target.value); setHele(false); }}
              disabled={busy}
              className="text-sm font-semibold text-foreground bg-secondary border border-border rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#FF2A6D] disabled:opacity-50"
            >
              {UGE_DAGE.map((d) => <option key={d.val} value={d.val}>{d.label}</option>)}
            </select>
          </div>
        </div>

        {/* Markering */}
        <div className="mb-5">
          <p className="text-[13px] font-semibold text-foreground mb-2">Markering</p>
          <label className="flex items-start gap-2 text-[13px] text-foreground cursor-pointer mb-2">
            <input type="radio" name="ugemark" checked={mark === "ferie"} onChange={() => setMark("ferie")} disabled={busy} className="mt-0.5" />
            <span>Ferie<span className="text-muted-foreground"> — Eleven får 0 timer (eller delvist)</span></span>
          </label>
          <label className="flex items-start gap-2 text-[13px] text-foreground cursor-pointer">
            <input type="radio" name="ugemark" checked={mark === "dobbelt"} onChange={() => setMark("dobbelt")} disabled={busy} className="mt-0.5" />
            <span>Dobbelttimer<span className="text-muted-foreground"> — Eleven får dobbelt timer (eller delvist)</span></span>
          </label>
        </div>

        <div className="flex items-center gap-2">
          <button
            disabled={busy}
            onClick={() => onSave(mark === "ferie" ? "set_ferie" : "set_dobbelt", { dage, fra })}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[13px] font-semibold bg-[#FF2A6D] text-white hover:bg-[#e02460] transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}Gem
          </button>
          <button
            disabled={busy}
            onClick={onClear}
            className="px-4 py-1.5 rounded-full text-[13px] font-semibold text-muted-foreground hover:text-foreground border border-border transition-colors disabled:opacity-50"
          >
            Ryd
          </button>
          <button
            disabled={busy}
            onClick={onClose}
            className="px-4 py-1.5 rounded-full text-[13px] font-semibold text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 ml-auto"
          >
            Annullér
          </button>
        </div>
      </div>
    </ModalPortal>
  );
}

// Foldbar ugetabel (Opfølgning #30 / #46.6). Viser ÅR/UGE/TIMER UGEN/AKK. MANGLENDE.
// Admin (eller kan_redigere_elever) kan klikke en uge for at markere Ferie eller
// Dobbelttimer via dialogen. Elever ser tabellen read-only.
function UgetabelCard({ ugetabel, isAdmin, onWeekAction }) {
  const [open, setOpen] = useState(false);
  const [openWeek, setOpenWeek] = useState(null);   // iso for åben uge-dialog
  const [weekSaving, setWeekSaving] = useState(null);

  const ut = ugetabel || {};
  const rows = ut.rows || [];
  const activeRow = openWeek ? rows.find((r) => r.iso === openWeek) : null;

  const runAction = async (iso, action, value) => {
    if (!isAdmin) return;
    setWeekSaving(iso);
    try {
      await onWeekAction(iso, action, value);
      setOpenWeek(null);
    } finally {
      setWeekSaving(null);
    }
  };

  // TIMER UGEN-celle: gl. ferie-label vinder (back-compat), ellers tal.
  const timerCell = (r) => {
    if (r.label) return r.label;
    return formatTimer(r.timer);
  };

  // Tom/edge-tilstande styret af backend-flags.
  let melding = null;
  if (ut.start_missing) melding = "Tilføj en startdato for at beregne ugetabellen.";
  else if (ut.weekly_zero) melding = "Angiv timer pr. uge for at beregne ugetabellen.";
  else if (ut.finished) melding = "Uddannelsen er gennemført — alle normerede timer er nået.";
  else if (ut.capped) melding = "Beregningen overstiger 10 år — tjek timer pr. uge og ferier.";

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden mt-4">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-secondary/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-purple-500/10 flex items-center justify-center">
            <Calendar className="w-4 h-4 text-purple-500" />
          </div>
          <span className="text-sm font-bold text-foreground uppercase tracking-wider">Ugetabel</span>
        </div>
        <span className="flex items-center gap-2 text-[13px] font-semibold text-muted-foreground">
          {open ? "Skjul ugetabel" : "Vis ugetabel"}
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </button>

      {open && (
        <div className="px-5 pb-5">
          {melding ? (
            <p className={`text-[13px] rounded-xl px-4 py-3 ${
              ut.finished ? "text-green-600 dark:text-green-400 bg-green-500/10 font-semibold" : "text-muted-foreground bg-secondary"
            }`}>
              {melding}
            </p>
          ) : rows.length === 0 ? (
            <p className="text-[13px] text-muted-foreground bg-secondary rounded-xl px-4 py-3">
              Ingen ugetabel tilgængelig endnu.
            </p>
          ) : (
            <>
              {isAdmin && (
                <p className="text-[13px] text-muted-foreground mb-3">
                  Klik på en uge for at markere Ferie eller Dobbelttimer.
                </p>
              )}
              <div className="border border-border rounded-xl overflow-hidden">
                <div className="max-h-[360px] overflow-y-auto">
                  <table className="w-full text-[13px] border-collapse">
                    <thead className="sticky top-0 bg-secondary z-10">
                      <tr className="text-muted-foreground">
                        <th className="text-left font-semibold uppercase tracking-wider px-3 py-2">År</th>
                        <th className="text-left font-semibold uppercase tracking-wider px-3 py-2">Uge</th>
                        <th className="text-left font-semibold uppercase tracking-wider px-3 py-2">Timer ugen</th>
                        <th className="text-right font-semibold uppercase tracking-wider px-3 py-2">Akk. manglende</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => {
                        const isBusy = weekSaving === r.iso;
                        const isFerie = r.mark === "ferie";
                        const isDobbelt = r.mark === "dobbelt";
                        return (
                          <tr
                            key={r.iso}
                            onClick={isAdmin ? () => setOpenWeek(r.iso) : undefined}
                            className={`border-t border-border ${
                              r.is_final
                                ? "bg-[#FF2A6D]/10 font-bold text-[#FF2A6D]"
                                : (r.label || isFerie)
                                  ? "text-muted-foreground italic"
                                  : "text-foreground"
                            } ${isAdmin ? "cursor-pointer hover:bg-secondary/40" : ""}`}
                          >
                            <td className="px-3 py-2 tabular-nums">{r.aar}</td>
                            <td className="px-3 py-2 tabular-nums">{r.uge}</td>
                            <td
                              className={`px-3 py-2 ${r.label ? "" : "tabular-nums"}`}
                              title={r.helligdage?.length ? r.helligdage.join(", ") : undefined}
                            >
                              {timerCell(r)}
                              {isFerie && (
                                <span className="ml-1.5 text-[11px] text-muted-foreground">Ferie</span>
                              )}
                              {isDobbelt && (
                                <span className="ml-1.5 text-[11px] font-bold px-1.5 py-0.5 rounded bg-green-500/10 text-green-600 dark:text-green-400">×2</span>
                              )}
                              {r.reduced && !r.label && (
                                <span className="ml-1.5 text-[11px] text-muted-foreground">(red.)</span>
                              )}
                              {r.helligdage?.length > 0 && (
                                <span className="ml-1.5 text-[11px] text-purple-500">helligdag</span>
                              )}
                              {isBusy && <Loader2 className="inline w-3 h-3 animate-spin ml-1.5 text-muted-foreground" />}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums font-semibold">
                              {formatTimer(r.akk_manglende)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {isAdmin && activeRow && (
        <UgeDialog
          row={activeRow}
          busy={weekSaving === activeRow.iso}
          onSave={(action, value) => runAction(activeRow.iso, action, value)}
          onClear={() => runAction(activeRow.iso, "clear_label", null)}
          onClose={() => { if (weekSaving !== activeRow.iso) setOpenWeek(null); }}
        />
      )}
    </div>
  );
}

function UddannelseCard({ info, afdeling, isAdmin, canEditUgetabel = isAdmin, adminToken, appItemId, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  if (!info) return null;
  const typeName = info.uddannelse_type === "kos" ? "Kosmetolog" : info.uddannelse_type === "fri" ? "Frisør" : "";
  const startLabel = info.start_date
    ? new Date(info.start_date).toLocaleDateString("da-DK", { day: "numeric", month: "long", year: "numeric" })
    : "—";
  const weeklyH = info.weekly_hours > 0 ? `${info.weekly_hours.toLocaleString("da-DK")} timer` : "—";
  const examLabel = info.expected_exam ? formatExamMonth(info.expected_exam) : "—";

  const handleEdit = () => {
    setDraft({
      start_date: info.start_date || "",
      weekly_hours: info.weekly_hours || "",
    });
    setEditing(true);
  };

  const handleSave = async () => {
    if (!adminToken || !appItemId) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/elev/stamdata/${appItemId}`, draft, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      const updates = { ...draft };
      if (res.data.expected_exam !== undefined) updates.expected_exam = res.data.expected_exam;
      if (res.data.expected_end_month !== undefined) updates.expected_end_month = res.data.expected_end_month;
      if (res.data.ugetabel !== undefined) updates.ugetabel = res.data.ugetabel;
      onUpdate && onUpdate(updates);
      setEditing(false);
      setDraft({});
    } catch (err) {
      alert("Kunne ikke gemme: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  // Ugetabel-redigering (Opfølgning #30): marker ferie / reducerede timer /
  // tilføj helligdag. Backend returnerer den friske tabel + forventet eksamen,
  // som vi merger ind i info så tabellen og eksamen-rækken opdateres live.
  const handleWeekAction = async (iso, action, value) => {
    // Opfølgning #35 (#2): ugetabel-redigering tilladt for admin ELLER medarbejder
    // med kan_se_elever (Elev-stamkort se+redigere). Backend gater tilsvarende.
    if (!canEditUgetabel || !adminToken || !appItemId) return;
    try {
      const res = await axios.put(
        `${API}/admin/elev/stamdata/${appItemId}/uge`,
        { iso, action, value },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      onUpdate && onUpdate({
        ugetabel: res.data.ugetabel,
        expected_exam: res.data.expected_exam,
        expected_end_month: res.data.expected_end_month,
      });
    } catch (err) {
      alert("Kunne ikke gemme uge: " + (err.response?.data?.detail || err.message));
      throw err;
    }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-purple-500/10 flex items-center justify-center">
            <GraduationCap className="w-4 h-4 text-purple-500" />
          </div>
          <h3 className="text-sm font-bold text-foreground uppercase tracking-wider">Uddannelse</h3>
        </div>
        {/* #46.5: medarbejdere med "Elev-stamkort (rediger)" (kan_redigere_elever)
            får samme redigerings-adgang til Uddannelse-boksen som SuperAdmin —
            inkl. Timer pr. uge, Startdato (og øvrige felter i edit-mode). */}
        {canEditUgetabel && appItemId && !editing && (
          <button
            onClick={handleEdit}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full text-[13px] font-semibold text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
          >
            <Pencil className="w-3 h-3" />
            Rediger
          </button>
        )}
      </div>
      <div>
        {typeName && <InfoRow label={<span className="flex items-center gap-1.5"><GraduationCap className="w-3.5 h-3.5" />Type</span>} value={typeName} />}
        {afdeling && <InfoRow label={<span className="flex items-center gap-1.5"><Building className="w-3.5 h-3.5" />Skole</span>} value={afdeling} />}

        {editing ? (
          <>
            <div className="flex justify-between items-center py-2 border-b border-border gap-3">
              <span className="text-sm text-muted-foreground flex items-center gap-1.5 shrink-0"><Calendar className="w-3.5 h-3.5" />Startdato</span>
              <input
                type="date"
                value={draft.start_date || ""}
                onChange={(e) => setDraft(prev => ({ ...prev, start_date: e.target.value }))}
                className="text-sm font-semibold text-foreground bg-secondary border border-border rounded-lg px-2.5 py-1.5 text-right w-full max-w-[220px] focus:outline-none focus:ring-1 focus:ring-[#FF2A6D] transition-colors"
              />
            </div>
            <div className="flex justify-between items-center py-2 border-b border-border gap-3">
              <span className="text-sm text-muted-foreground flex items-center gap-1.5 shrink-0"><Clock className="w-3.5 h-3.5" />Timer pr. uge</span>
              <input
                type="number"
                step="0.5"
                value={draft.weekly_hours || ""}
                onChange={(e) => setDraft(prev => ({ ...prev, weekly_hours: e.target.value }))}
                className="text-sm font-semibold text-foreground bg-secondary border border-border rounded-lg px-2.5 py-1.5 text-right w-full max-w-[220px] focus:outline-none focus:ring-1 focus:ring-[#FF2A6D] transition-colors"
              />
            </div>
          </>
        ) : (
          <>
            <InfoRow label={<span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />Startdato</span>} value={startLabel} />
            <InfoRow label={<span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />Timer pr. uge</span>} value={weeklyH} />
          </>
        )}

        <InfoRow
          label={<span className="flex items-center gap-1.5"><GraduationCap className="w-3.5 h-3.5" />Forventet eksamen</span>}
          value={examLabel}
          highlight
        />
      </div>

      {/* Foldbar ugetabel (Opfølgning #30/#35) — redigerbar for admin + kan_se_elever */}
      {info.ugetabel && (
        <UgetabelCard
          ugetabel={info.ugetabel}
          isAdmin={canEditUgetabel}
          onWeekAction={handleWeekAction}
        />
      )}

      {editing && (
        <div className="flex items-center gap-2 mt-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold bg-[#FF2A6D] text-white hover:bg-[#e02460] transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Gem
          </button>
          <button
            onClick={() => { setEditing(false); setDraft({}); }}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
          >
            <X className="w-3 h-3" />
            Fortryd
          </button>
        </div>
      )}
    </div>
  );
}

function PakkeCard({ pakke }) {
  const erKontant = pakke.pris_pr_maaned === 0;
  const nettoOpstart = pakke.opstartsgebyr - (pakke.opstartsgebyr_rabat || 0);

  return (
    <div className="bg-card border border-border rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-full bg-[#FF2A6D]/10 flex items-center justify-center">
          <Package className="w-4 h-4 text-[#FF2A6D]" />
        </div>
        <h3 className="text-sm font-bold text-foreground uppercase tracking-wider">Din pakke</h3>
      </div>

      <p className="text-base font-semibold text-foreground mb-4">{pakke.titel}</p>

      <div>
        {pakke.varighed_maaneder > 0 && (
          <InfoRow label="Varighed" value={`${pakke.varighed_maaneder} måneder`} />
        )}
        {nettoOpstart > 0 && (
          <InfoRow
            label={pakke.opstartsgebyr_rabat > 0 ? `Opstartsgebyr (efter rabat)` : "Opstartsgebyr"}
            value={formatKr(nettoOpstart)}
          />
        )}
        {pakke.opstartsgebyr_rabat > 0 && (
          <InfoRow label="Rabat opstartsgebyr" value={`-${formatKr(pakke.opstartsgebyr_rabat)}`} />
        )}
        {erKontant ? (
          <InfoRow label="Betalingsform" value="Kontant betaling" highlight />
        ) : (
          <InfoRow label="Månedlig betaling" value={formatKr(pakke.pris_pr_maaned)} highlight />
        )}
        {pakke.kontant_rabat > 0 && (
          <InfoRow label="Kontantrabat" value={`-${formatKr(pakke.kontant_rabat)}`} />
        )}
      </div>
    </div>
  );
}

function EconomicCard({ economic }) {
  if (!economic) return null;

  return (
    <div className="bg-card border border-border rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center">
          <CreditCard className="w-4 h-4 text-blue-400" />
        </div>
        <h3 className="text-sm font-bold text-foreground uppercase tracking-wider">Betalingsoversigt</h3>
      </div>

      <div className="mb-4">
        <InfoRow label="Debitor" value={economic.debitor_navn || `#${economic.debitor_nummer}`} />
        <InfoRow
          label="Samlet saldo"
          value={formatKr(economic.saldo)}
          highlight={economic.saldo > 0}
        />
      </div>

      {economic.fakturaer?.length > 0 && (
        <div>
          <p className="text-[13px] text-muted-foreground uppercase tracking-wider mb-3">Seneste fakturaer</p>
          <div className="space-y-2">
            {economic.fakturaer.map((inv) => (
              <div
                key={inv.nummer}
                className={`flex items-center justify-between p-3 rounded-xl border ${
                  inv.restbeloeb > 0
                    ? "border-[#FF2A6D]/30 bg-[#FF2A6D]/5"
                    : "border-border bg-secondary"
                }`}
              >
                <div>
                  <p className="text-[13px] font-semibold text-foreground">Faktura #{inv.nummer}</p>
                  <p className="text-[13px] text-muted-foreground">
                    {formatDate(inv.dato)} · Forfald {formatDate(inv.forfald)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[13px] font-semibold text-foreground">{formatKr(inv.beloeb)}</p>
                  {inv.restbeloeb > 0 ? (
                    <p className="text-[13px] text-[#FF2A6D] font-semibold">Resterer {formatKr(inv.restbeloeb)}</p>
                  ) : (
                    <div className="flex items-center gap-1 justify-end">
                      <CheckCircle className="w-3 h-3 text-green-600 dark:text-green-400" />
                      <p className="text-[13px] text-green-600 dark:text-green-400">Betalt</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {economic.fakturaer?.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-4">Ingen fakturaer fundet</p>
      )}
    </div>
  );
}

/* ─── Vipps Recurring abonnement ─── */

const VIPPS_STATUS_LABEL = {
  ACTIVE: "Aktivt",
  PENDING: "Afventer godkendelse",
  STOPPED: "Stoppet",
  EXPIRED: "Udløbet",
  UNKNOWN: "Ukendt",
};

const VIPPS_STATUS_COLOR = {
  ACTIVE: "text-green-600 dark:text-green-400 bg-green-500/10 border-green-500/20",
  PENDING: "text-amber-600 dark:text-amber-400 bg-amber-500/10 border-amber-500/20",
  STOPPED: "text-red-500 bg-red-500/10 border-red-500/20",
  EXPIRED: "text-muted-foreground bg-secondary border-border",
  UNKNOWN: "text-muted-foreground bg-secondary border-border",
};

function VippsAbonnementCard({ token, vipps, pakke, onAgreementCreated }) {
  const [loading, setLoading] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState(null);
  const [confirmStop, setConfirmStop] = useState(false);

  const configured = vipps?.configured;
  const agreement = vipps?.agreement_id ? vipps : null;
  const status = vipps?.agreement_status;

  // Vis altid — selv hvis pakke mangler eller er kontant
  // (kortet viser blot "ikke aktiveret" i så fald)

  const handleOpret = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API}/dashboard/student/vipps/create-agreement`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const confirmUrl = res.data.confirmation_url;
      if (confirmUrl) {
        onAgreementCreated && onAgreementCreated(res.data);
        window.location.href = confirmUrl;
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Kunne ikke oprette abonnement. Prøv igen.");
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async () => {
    setStopping(true);
    setError(null);
    try {
      await axios.post(
        `${API}/dashboard/student/vipps/stop-agreement`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onAgreementCreated && onAgreementCreated({ agreement_status: "STOPPED" });
      setConfirmStop(false);
    } catch (err) {
      setError(err.response?.data?.detail || "Kunne ikke stoppe abonnement.");
    } finally {
      setStopping(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-full bg-[#FF5B24]/10 flex items-center justify-center">
          {/* Vipps orange farve */}
          <RefreshCw className="w-4 h-4 text-[#FF5B24]" />
        </div>
        <h3 className="text-sm font-bold text-foreground uppercase tracking-wider">Fast månedligt abonnement</h3>
      </div>

      {!configured && !vipps?.agreement_id ? (
        <p className="text-sm text-muted-foreground">
          Fast månedligt abonnement via MobilePay er ikke aktiveret endnu.
        </p>
      ) : status === "ACTIVE" ? (
        /* ── Aktivt abonnement ── */
        <div>
          <div className="flex items-center justify-between mb-3">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[13px] font-semibold border ${VIPPS_STATUS_COLOR.ACTIVE}`}>
              <CheckCircle className="w-3 h-3" />
              {VIPPS_STATUS_LABEL.ACTIVE}
            </span>
            <span className="text-sm font-semibold text-foreground">{formatKr(vipps.amount_kr)}/md.</span>
          </div>
          <p className="text-[13px] text-muted-foreground mb-4">
            Dit abonnement er aktivt. Beløbet trækkes automatisk den 1. i hver måned via MobilePay.
          </p>
          {!confirmStop ? (
            <button
              onClick={() => setConfirmStop(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-semibold text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors border border-border hover:border-red-500/30"
            >
              <Square className="w-3 h-3" />
              Stop abonnement
            </button>
          ) : (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20">
              <p className="text-[13px] text-red-500 font-semibold mb-2">Er du sikker på, at du vil stoppe dit abonnement?</p>
              <div className="flex gap-2">
                <button
                  onClick={handleStop}
                  disabled={stopping}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50"
                >
                  {stopping ? <Loader2 className="w-3 h-3 animate-spin" /> : <Square className="w-3 h-3" />}
                  Ja, stop
                </button>
                <button
                  onClick={() => setConfirmStop(false)}
                  className="px-3 py-1.5 rounded-full text-[13px] font-semibold text-muted-foreground hover:bg-secondary transition-colors"
                >
                  Fortryd
                </button>
              </div>
            </div>
          )}
        </div>
      ) : status === "PENDING" ? (
        /* ── Afventer godkendelse ── */
        <div>
          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[13px] font-semibold border mb-3 ${VIPPS_STATUS_COLOR.PENDING}`}>
            <Loader2 className="w-3 h-3 animate-spin" />
            {VIPPS_STATUS_LABEL.PENDING}
          </div>
          <p className="text-[13px] text-muted-foreground">
            Åbn MobilePay-appen og godkend abonnementet for at aktivere automatisk månedlig betaling.
          </p>
        </div>
      ) : (
        /* ── Intet aktivt / stoppet / udløbet ── */
        <div>
          {status === "STOPPED" && (
            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[13px] font-semibold border mb-3 ${VIPPS_STATUS_COLOR.STOPPED}`}>
              <Square className="w-3 h-3" />
              {VIPPS_STATUS_LABEL.STOPPED}
            </div>
          )}
          <p className="text-sm text-muted-foreground mb-4">
            {status === "STOPPED"
              ? "Dit abonnement er stoppet. Du kan oprette et nyt herunder."
              : pakke?.pris_pr_maaned > 0
                ? `Opret et fast månedligt abonnement på ${formatKr(pakke.pris_pr_maaned)} via MobilePay. Beløbet trækkes automatisk den 1. i måneden.`
                : "Opret et fast månedligt abonnement via MobilePay."}
          </p>
          <button
            onClick={handleOpret}
            disabled={loading}
            className="flex items-center justify-center gap-2 w-full py-3 rounded-xl text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: "#FF5B24" }}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Opretter abonnement...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Opret abonnement via MobilePay
              </>
            )}
          </button>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 p-3 bg-[#FF2A6D]/10 border border-[#FF2A6D]/20 rounded-xl mt-3">
          <AlertCircle className="w-4 h-4 text-[#FF2A6D] mt-0.5 shrink-0" />
          <p className="text-[13px] text-[#FF2A6D]">{error}</p>
        </div>
      )}
    </div>
  );
}

function MobilePayButton({ token, pakke }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [payUrl, setPayUrl] = useState(null);

  if (!pakke || pakke.pris_pr_maaned <= 0) return null;

  const handlePay = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API}/dashboard/student/mobilepay/init`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const url = res.data.redirect_url;
      if (url) {
        setPayUrl(url);
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Betalingen kunne ikke oprettes. Prøv igen.");
    } finally {
      setLoading(false);
    }
  };

  const now = new Date();
  const maanedNavn = now.toLocaleDateString("da-DK", { month: "long", year: "numeric" });

  return (
    <div className="bg-card border border-border rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-full bg-[#5A78FF]/10 flex items-center justify-center">
          <Smartphone className="w-4 h-4 text-[#5A78FF]" />
        </div>
        <h3 className="text-sm font-bold text-foreground uppercase tracking-wider">Betal med MobilePay</h3>
      </div>

      <p className="text-sm text-muted-foreground mb-4">
        Betal din månedlige skolepenge for{" "}
        <span className="text-foreground font-semibold">{maanedNavn}</span> på{" "}
        <span className="text-foreground font-semibold">{formatKr(pakke.pris_pr_maaned)}</span>.
      </p>

      {error && (
        <div className="flex items-start gap-2 p-3 bg-[#FF2A6D]/10 border border-[#FF2A6D]/20 rounded-xl mb-4">
          <AlertCircle className="w-4 h-4 text-[#FF2A6D] mt-0.5 shrink-0" />
          <p className="text-[13px] text-[#FF2A6D]">{error}</p>
        </div>
      )}

      {payUrl ? (
        <a
          href={payUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-[#5A78FF] text-white text-sm font-semibold hover:bg-[#4A68EF] transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
          Åbn MobilePay
        </a>
      ) : (
        <button
          onClick={handlePay}
          disabled={loading}
          className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-[#5A78FF] text-white text-sm font-semibold hover:bg-[#4A68EF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Opretter betaling...
            </>
          ) : (
            <>
              <Smartphone className="w-4 h-4" />
              Betal {formatKr(pakke.pris_pr_maaned)} med MobilePay
            </>
          )}
        </button>
      )}
    </div>
  );
}

/* ─── Prøver (filer fra Podio) ─── */
function ProverCard({ prover, adminToken, appItemId, isAdmin }) {
  const [expanded, setExpanded] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const handleSync = async () => {
    if (!isAdmin || !adminToken || !appItemId) return;
    setSyncing(true);
    try {
      await axios.post(`${API}/admin/elev/stamdata/${appItemId}/sync-prover`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
    } catch { /* ignore */ }
    setSyncing(false);
  };

  const handleDownload = (fileId, fileName) => {
    const url = `${API}/podio/image/${fileId}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.target = "_blank";
    a.click();
  };

  const visible = expanded ? (prover || []) : (prover || []).slice(0, 5);

  return (
    <CollapsibleCard title="Prøver" icon={FileText} iconBg="bg-purple-500/10" iconColor="text-purple-400" badge={(prover || []).length || undefined}>
      {isAdmin && (
        <div className="flex justify-end mb-3">
          <button onClick={handleSync} disabled={syncing} className="text-[13px] text-muted-foreground hover:text-foreground flex items-center gap-1" title="Synk fra Podio">
            <RefreshCw className={`w-3 h-3 ${syncing ? "animate-spin" : ""}`} />
            Synk
          </button>
        </div>
      )}
      {(!prover || prover.length === 0) ? (
        <p className="text-sm text-muted-foreground text-center py-3">Ingen prøver registreret endnu</p>
      ) : (
        <>
          <div className="space-y-2">
            {visible.map((f) => (
              <div key={f.file_id} className="flex items-center justify-between p-2.5 bg-secondary rounded-xl border border-border">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="text-sm text-foreground truncate">{f.name}</span>
                </div>
                {f.file_id && (
                  <button onClick={() => handleDownload(f.file_id, f.name)} className="text-muted-foreground hover:text-foreground shrink-0 ml-2" title="Åbn/download">
                    <Download className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          {(prover || []).length > 5 && (
            <button onClick={() => setExpanded(!expanded)} className="mt-3 w-full flex items-center justify-center gap-1 text-[13px] text-muted-foreground hover:text-foreground transition-colors">
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              {expanded ? "Vis færre" : `Vis alle ${prover.length} filer`}
            </button>
          )}
        </>
      )}
    </CollapsibleCard>
  );
}

/* ─── Karakterer (løbende prøver) ─── */
const KARAKTER_OPTIONS = ["-3", "00", "02", "4", "7", "10", "12", "Bestået", "Ikke bestået"];

function beregnGennemsnit(karakterer) {
  const numericMap = { "-3": -3, "00": 0, "02": 2, "4": 4, "7": 7, "10": 10, "12": 12 };
  const nums = (karakterer || [])
    .map(k => numericMap[k.karakter])
    .filter(n => n !== undefined && n !== null);
  if (nums.length === 0) return null;
  return (nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(1);
}

/* ─── Hjælper: karakter-badge farve ─── */
function KarakterBadge({ karakter }) {
  if (!karakter) return <span className="text-[13px] text-muted-foreground italic">N/A</span>;
  const lower = karakter.toLowerCase();
  const cls =
    ["-3", "00", "02"].includes(karakter) ? "text-red-500 bg-red-500/10 border-red-500/20" :
    ["4", "7"].includes(karakter) ? "text-amber-500 bg-amber-500/10 border-amber-500/20" :
    ["10", "12"].includes(karakter) ? "text-green-600 dark:text-green-400 bg-green-500/10 border-green-500/20" :
    lower === "bestået" ? "text-green-600 dark:text-green-400 bg-green-500/10 border-green-500/20" :
    lower === "ikke bestået" ? "text-red-500 bg-red-500/10 border-red-500/20" :
    "text-foreground bg-secondary border-border";
  return <span className={`text-sm font-bold px-2 py-0.5 rounded-lg border ${cls}`}>{karakter}</span>;
}

/* ─── Hjælper: enkelt redigerbar karakter-række ─── */
function KarakterRow({ idx, entry, isAdmin, adminToken, appItemId, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const handleEdit = () => {
    setDraft({ emne: entry.emne, karakter: entry.karakter || "", dato: entry.dato || "" });
    setEditing(true);
  };
  const handleCancel = () => setEditing(false);
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/admin/elev/stamdata/${appItemId}/karakterer/${idx}`,
        draft,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      onSaved && onSaved(idx, res.data.entry);
      setEditing(false);
    } catch { /* ignore */ }
    setSaving(false);
  };

  if (editing) {
    return (
      <div className="py-2 border-b border-border last:border-0 space-y-1.5">
        <input
          type="text"
          value={draft.emne}
          onChange={e => setDraft(d => ({ ...d, emne: e.target.value }))}
          className="w-full text-sm bg-secondary border border-border rounded-lg px-2.5 py-1 focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
          placeholder="Emne"
        />
        <div className="flex gap-2">
          <select
            value={draft.karakter}
            onChange={e => setDraft(d => ({ ...d, karakter: e.target.value }))}
            className="flex-1 appearance-none text-sm bg-background border border-border rounded-lg px-2.5 h-8 focus:outline-none focus:ring-1 focus:ring-[#FF2A6D] text-foreground"
          >
            <option value="">N/A</option>
            {KARAKTER_OPTIONS.map(k => <option key={k} value={k}>{k}</option>)}
          </select>
          <input
            type="date"
            value={draft.dato}
            onChange={e => setDraft(d => ({ ...d, dato: e.target.value }))}
            className="flex-1 text-sm bg-background border border-border rounded-lg px-2.5 h-8 focus:outline-none focus:ring-1 focus:ring-[#FF2A6D] text-foreground"
          />
        </div>
        <div className="flex gap-2">
          <button onClick={handleSave} disabled={saving} className="flex-1 py-1 rounded-lg bg-[#FF2A6D] text-white text-[13px] font-semibold disabled:opacity-50">
            {saving ? "Gemmer..." : "Gem"}
          </button>
          <button onClick={handleCancel} className="flex-1 py-1 rounded-lg bg-secondary text-[13px] font-semibold border border-border">Annuller</button>
        </div>
        {entry.sidst_rettet_af && (
          <p className="text-[13px] text-muted-foreground/60">Sidst rettet af: {entry.sidst_rettet_af}</p>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between py-2 border-b border-border last:border-0 group">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-foreground">{entry.emne}</p>
        <div className="flex items-center gap-2 flex-wrap">
          {entry.dato && <p className="text-[13px] text-muted-foreground">{formatDate(entry.dato)}</p>}
          {entry.sidst_rettet_af && (
            <p className="text-[13px] text-muted-foreground/60">Af: {entry.sidst_rettet_af}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <KarakterBadge karakter={entry.karakter} />
        {isAdmin && (
          <button
            onClick={handleEdit}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── Karakterer (løbende prøver) ─── */
function KaraktererCard({ karakterer, adminToken, appItemId, isAdmin, onUpdate }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ emne: "", karakter: "", dato: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showEksamen, setShowEksamen] = useState(false);

  // Grupper: Grundmodul vs. øvrige
  const GM_KEYWORDS = ["gm1", "gm2", "gm3", "grundmodul", "skriftlig gm", "skriftlig grundmodul"];
  const isGrundmodul = (entry) => {
    const emne = (entry.emne || "").toLowerCase();
    return GM_KEYWORDS.some(kw => emne.includes(kw));
  };

  const grundmodul = (karakterer || []).filter(isGrundmodul);
  const oevrige = (karakterer || []).filter(e => !isGrundmodul(e));

  const gennemsnit = beregnGennemsnit(karakterer);

  const handleAdd = async () => {
    if (!addForm.emne) { setError("Emne er påkrævet"); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API}/admin/elev/stamdata/${appItemId}/karakterer`,
        addForm,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      onUpdate && onUpdate(res.data.entry);
      setAddForm({ emne: "", karakter: "", dato: "" });
      setShowAddForm(false);
    } catch (e) {
      setError(e.response?.data?.detail || "Fejl ved gemning");
    } finally {
      setSaving(false);
    }
  };

  const handleRowSaved = (idx, entry) => {
    onUpdate && onUpdate(null, null, idx, entry);
  };

  const renderGroup = (title, entries, startIdx) => {
    if (entries.length === 0) return null;
    return (
      <div className="mb-4">
        <p className="text-[13px] font-semibold text-muted-foreground uppercase tracking-wider mb-2 pb-1 border-b border-border">{title}</p>
        {entries.map((k, i) => (
          <KarakterRow
            key={i}
            idx={startIdx + i}
            entry={k}
            isAdmin={isAdmin}
            adminToken={adminToken}
            appItemId={appItemId}
            onSaved={handleRowSaved}
          />
        ))}
      </div>
    );
  };

  const badgeEl = gennemsnit !== null ? `Ø ${gennemsnit}` : undefined;

  return (
    <CollapsibleCard title="Karakterer" icon={Award} iconBg="bg-amber-500/10" iconColor="text-amber-400" badge={badgeEl}>
      {isAdmin && (
        <div className="flex justify-end mb-3">
          <button onClick={() => setShowAddForm(!showAddForm)} className="text-[13px] text-muted-foreground hover:text-foreground flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" />Tilføj
          </button>
        </div>
      )}

      {isAdmin && showAddForm && (
        <div className="mb-4 p-3 bg-secondary rounded-xl border border-border space-y-2">
          <input type="text" placeholder="Emne" value={addForm.emne}
            onChange={e => setAddForm(f => ({ ...f, emne: e.target.value }))}
            className="w-full text-sm bg-card border border-border rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
          <div className="flex gap-2">
            <select value={addForm.karakter} onChange={e => setAddForm(f => ({ ...f, karakter: e.target.value }))}
              className="flex-1 appearance-none text-sm bg-background border border-border rounded-lg px-3 h-8 focus:outline-none focus:ring-1 focus:ring-[#FF2A6D] text-foreground">
              <option value="">N/A (endnu ingen karakter)</option>
              {KARAKTER_OPTIONS.map(k => <option key={k} value={k}>{k}</option>)}
            </select>
            <input type="date" value={addForm.dato} onChange={e => setAddForm(f => ({ ...f, dato: e.target.value }))}
              className="flex-1 text-sm bg-background border border-border rounded-lg px-3 h-8 focus:outline-none focus:ring-1 focus:ring-[#FF2A6D] text-foreground" />
          </div>
          {error && <p className="text-[13px] text-[#FF2A6D]">{error}</p>}
          <div className="flex gap-2">
            <button onClick={handleAdd} disabled={saving} className="flex-1 py-1.5 rounded-lg bg-[#FF2A6D] text-white text-[13px] font-semibold disabled:opacity-50">{saving ? "Gemmer..." : "Gem"}</button>
            <button onClick={() => { setShowAddForm(false); setError(null); }} className="flex-1 py-1.5 rounded-lg bg-secondary text-[13px] font-semibold border border-border">Annuller</button>
          </div>
        </div>
      )}

      {(!karakterer || karakterer.length === 0) ? (
        <p className="text-sm text-muted-foreground text-center py-3">Ingen karakterer registreret endnu</p>
      ) : (
        <>
          {renderGroup("Grundmodul", grundmodul, 0)}
          {renderGroup("Øvrige prøver", oevrige, grundmodul.length)}
        </>
      )}

      {isAdmin && (
        <button onClick={() => setShowEksamen(!showEksamen)}
          className="mt-3 w-full flex items-center justify-center gap-1.5 text-[13px] font-semibold text-amber-500 hover:text-amber-400 transition-colors border-t border-border pt-3">
          <Award className="w-3.5 h-3.5" />
          {showEksamen ? "Skjul afsluttende eksamen" : "Afsluttende eksamen →"}
        </button>
      )}
      {isAdmin && showEksamen && <EksamenInlineView adminToken={adminToken} appItemId={appItemId} />}
    </CollapsibleCard>
  );
}

/* ─── Afsluttende eksamen (inline under KaraktererCard) ─── */
function EksamenInlineView({ adminToken, appItemId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/elev/stamdata/${appItemId}/eksamen`, {
      headers: { Authorization: `Bearer ${adminToken}` },
    }).then(res => {
      setData(res.data);
      const eks = res.data.eksamen;
      if (eks) {
        setForm({
          laegecensor: eks.laegecensor || false,
          censor: eks.censor || "",
          underviser: eks.underviser || "",
          dato: eks.dato || "",
          karakterer: eks.karakterer || [],
        });
      } else {
        setForm({
          laegecensor: false,
          censor: "",
          underviser: "",
          dato: "",
          karakterer: [],
        });
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [appItemId, adminToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const setEksKarakter = (emne, karakter) => {
    setForm(f => {
      const existing = f.karakterer.find(k => k.emne === emne);
      if (existing) {
        return { ...f, karakterer: f.karakterer.map(k => k.emne === emne ? { ...k, karakter } : k) };
      }
      return { ...f, karakterer: [...f.karakterer, { emne, karakter }] };
    });
  };

  const getEksKarakter = (emne) => form?.karakterer?.find(k => k.emne === emne)?.karakter || "";

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/elev/stamdata/${appItemId}/eksamen`, form, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch { /* ignore */ }
    setSaving(false);
  };

  if (loading) return <div className="mt-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>;
  if (!data || !form) return null;

  const uddType = data.uddannelsestype === "fri" ? "Frisør" : "Kosmetolog";
  const emner = data.afsluttende_emner || [];
  const undervisere = data.undervisere || [];

  return (
    <div className="mt-4 pt-4 border-t border-border space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[13px] font-semibold text-muted-foreground uppercase tracking-wider">Afsluttende eksamen · {uddType}</p>
        {data.eksamen?.registreret_af && (
          <p className="text-[13px] text-muted-foreground/60">Registreret af: {data.eksamen.registreret_af}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[13px] text-muted-foreground block mb-1">Dato</label>
          <input type="date" value={form.dato} onChange={e => setForm(f => ({ ...f, dato: e.target.value }))}
            className="w-full text-sm bg-background border border-border rounded-lg px-2.5 h-9 focus:outline-none focus:ring-1 focus:ring-[#FF2A6D] text-foreground" />
        </div>
        <div className="flex items-end pb-1">
          <label className="flex items-center gap-1.5 cursor-pointer h-9">
            <input type="checkbox" checked={form.laegecensor} onChange={e => setForm(f => ({ ...f, laegecensor: e.target.checked }))}
              className="w-4 h-4 accent-[#FF2A6D]" />
            <span className="text-sm text-foreground">Lægecensor</span>
          </label>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[13px] text-muted-foreground block mb-1">Censor</label>
          <input type="text" value={form.censor} onChange={e => setForm(f => ({ ...f, censor: e.target.value }))}
            placeholder="Navn på censor"
            className="w-full text-sm bg-background border border-border rounded-lg px-2.5 h-9 focus:outline-none focus:ring-1 focus:ring-[#FF2A6D] text-foreground" />
        </div>
        <div>
          <label className="text-[13px] text-muted-foreground block mb-1">Underviser</label>
          <select value={form.underviser} onChange={e => setForm(f => ({ ...f, underviser: e.target.value }))}
            className="w-full appearance-none text-sm bg-background border border-border rounded-lg px-2.5 h-9 focus:outline-none focus:ring-1 focus:ring-[#FF2A6D] text-foreground">
            <option value="">Vælg underviser</option>
            {undervisere.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
      </div>

      <div>
        <p className="text-[13px] text-muted-foreground mb-2">Karakterer</p>
        <div className="space-y-1.5">
          {emner.map(emne => (
            <div key={emne} className="flex items-center justify-between gap-2">
              <span className="text-sm text-foreground flex-1">{emne}</span>
              <select
                value={getEksKarakter(emne)}
                onChange={e => setEksKarakter(emne, e.target.value)}
                className="appearance-none text-sm bg-background border border-border rounded-lg px-2 h-8 focus:outline-none focus:ring-1 focus:ring-[#FF2A6D] text-foreground"
              >
                <option value="">N/A</option>
                {KARAKTER_OPTIONS.map(k => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
          ))}
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className={`w-full py-2 rounded-xl text-sm font-semibold transition-colors ${saved ? "bg-green-500/20 text-green-600 dark:text-green-400" : "bg-[#FF2A6D] text-white hover:bg-[#e02660]"} disabled:opacity-50`}
      >
        {saving ? "Gemmer..." : saved ? "Gemt ✓" : "Gem eksamen"}
      </button>
    </div>
  );
}

/* ─── Statussamtaler ─── */
function StatussamtalerCard({ statussamtaler: initialSamtaler, adminToken, appItemId, isAdmin, onUpdate }) {
  const [samtaler, setSamtaler] = useState(initialSamtaler || []);
  const [toggling, setToggling] = useState(null);

  // Admin: hent fuld statussamtaler med link + samtale_item_id fra admin endpoint
  useEffect(() => {
    if (!isAdmin || !adminToken || !appItemId) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/elev/stamdata/${appItemId}`, {
          headers: { Authorization: `Bearer ${adminToken}` },
        });
        if (res.data?.statussamtaler) setSamtaler(res.data.statussamtaler);
      } catch { /* fallback to initialSamtaler */ }
    })();
  }, [isAdmin, adminToken, appItemId]);

  const handleToggleAfholdt = async (samtale) => {
    if (!isAdmin) return;
    const newVal = !samtale.afholdt;
    setToggling(samtale.samtale_item_id);
    try {
      await axios.patch(
        `${API}/admin/elev/stamdata/${appItemId}/statussamtale/${samtale.samtale_item_id}`,
        { afholdt: newVal },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      setSamtaler(prev => prev.map(s =>
        s.samtale_item_id === samtale.samtale_item_id ? { ...s, afholdt: newVal } : s
      ));
      onUpdate && onUpdate(samtale.samtale_item_id, newVal);
    } catch { /* ignore */ }
    setToggling(null);
  };

  return (
    <CollapsibleCard title="Statussamtaler" icon={MessageSquare} iconBg="bg-teal-500/10" iconColor="text-teal-400" badge={samtaler.length || undefined}>
      {samtaler.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-3">Ingen statussamtaler registreret</p>
      ) : (
        <div className="space-y-3">
          {samtaler.map((s, i) => (
            <div key={s.samtale_item_id || i} className="p-3 bg-secondary rounded-xl border border-border">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-foreground">
                  Statussamtale {s.dato ? formatDate(s.dato) : `#${i + 1}`}
                </p>
                <div className="flex items-center gap-2">
                  {isAdmin ? (
                    <button
                      onClick={() => handleToggleAfholdt(s)}
                      disabled={toggling === s.samtale_item_id}
                      className={`text-[13px] px-2 py-0.5 rounded-full border font-semibold transition-colors ${
                        s.afholdt
                          ? "text-green-600 dark:text-green-400 bg-green-500/10 border-green-500/20 hover:bg-green-500/20"
                          : "text-muted-foreground bg-secondary border-border hover:bg-card"
                      }`}
                      title="Klik for at ændre afholdt-status"
                    >
                      {toggling === s.samtale_item_id ? "..." : s.afholdt ? "Afholdt" : "Ikke afholdt"}
                    </button>
                  ) : (
                    <span className={`text-[13px] px-2 py-0.5 rounded-full border font-semibold ${
                      s.afholdt ? "text-green-600 dark:text-green-400 bg-green-500/10 border-green-500/20" : "text-muted-foreground bg-secondary border-border"
                    }`}>
                      {s.afholdt ? "Afholdt" : "Ikke afholdt"}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-[13px] text-muted-foreground">
                <span className="flex items-center gap-1">
                  <BookOpen className="w-3 h-3" />
                  Tilfredshedsundersøgelse:{" "}
                  <span className={`font-semibold ml-1 ${s.sporgeskema_udfyldt ? "text-green-600 dark:text-green-400" : "text-foreground"}`}>
                    {s.sporgeskema_udfyldt ? "Udfyldt" : "Ikke udfyldt"}
                  </span>
                </span>
                {isAdmin && s.link && (
                  <a href={s.link} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 text-teal-500 hover:text-teal-400 transition-colors font-semibold">
                    <ExternalLink className="w-3 h-3" />
                    Åbn dokument
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </CollapsibleCard>
  );
}

/* ─── Fravær og mødestabilitet ─── */
function FravaerCard({ timerData, fravaerNote, adminToken, appItemId, isAdmin }) {
  const [note, setNote] = useState(fravaerNote || "");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!timerData) return null;

  const fravaer = timerData.fravær ?? 0;
  const fravaerPct = timerData.fravær_pct ?? 0;
  const sygdom = timerData.sygdom ?? 0;
  const udeblivelse = timerData.udeblivelse ?? 0;
  const moedePct = 100 - fravaerPct;

  const handleSaveNote = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/elev/stamdata/${appItemId}/fravaer-note`,
        { note },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      setEditing(false);
    } catch { /* ignore */ }
    setSaving(false);
  };

  return (
    <CollapsibleCard title="Fravær & Mødestabilitet" icon={Clock} iconBg="bg-rose-500/10" iconColor="text-rose-400">
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-secondary rounded-xl p-3 text-center">
          <p className="text-[13px] text-muted-foreground mb-1">Fravær i alt</p>
          <p className={`text-2xl font-bold ${fravaer > 0 ? "text-[#FF2A6D]" : "text-foreground"}`}>{fravaer.toFixed(1)}t</p>
        </div>
        <div className="bg-secondary rounded-xl p-3 text-center">
          <p className="text-[13px] text-muted-foreground mb-1">Mødestabilitet</p>
          <p className={`text-2xl font-bold ${moedePct < 90 ? "text-amber-500" : "text-green-600 dark:text-green-400"}`}>{moedePct.toFixed(0)}%</p>
        </div>
      </div>

      <div className="space-y-1.5 mb-4">
        <div className="flex justify-between text-sm"><span className="text-muted-foreground">Sygdom</span><span className="font-semibold">{sygdom.toFixed(1)}t</span></div>
        <div className="flex justify-between text-sm"><span className="text-muted-foreground">Udeblevet</span><span className="font-semibold">{udeblivelse.toFixed(1)}t</span></div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Fravær %</span>
          <span className={`font-semibold ${fravaerPct > 10 ? "text-[#FF2A6D]" : "text-foreground"}`}>{fravaerPct}%</span>
        </div>
      </div>

      {isAdmin ? (
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[13px] text-muted-foreground">Note</span>
            {!editing && <button onClick={() => setEditing(true)} className="text-[13px] text-muted-foreground hover:text-foreground flex items-center gap-1"><Pencil className="w-3 h-3" />Rediger</button>}
          </div>
          {editing ? (
            <div className="space-y-2">
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} placeholder="Tilføj note om fravær..."
                className="w-full text-sm bg-secondary border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#FF2A6D] resize-none" />
              <div className="flex gap-2">
                <button onClick={handleSaveNote} disabled={saving} className="flex-1 py-1.5 rounded-lg bg-[#FF2A6D] text-white text-[13px] font-semibold disabled:opacity-50">{saving ? "Gemmer..." : "Gem"}</button>
                <button onClick={() => setEditing(false)} className="flex-1 py-1.5 rounded-lg bg-secondary text-[13px] font-semibold border border-border">Annuller</button>
              </div>
            </div>
          ) : (
            note ? <p className="text-sm text-foreground bg-secondary rounded-lg px-3 py-2">{note}</p>
                 : <p className="text-[13px] text-muted-foreground italic">Ingen note</p>
          )}
        </div>
      ) : (
        fravaerNote && <p className="text-sm text-foreground bg-secondary rounded-lg px-3 py-2">{fravaerNote}</p>
      )}
    </CollapsibleCard>
  );
}

export function ElevOkonomiView({ token, adminToken, isAdmin, isSuperAdmin = isAdmin, canEditUgetabel = isAdmin }) {
  // `isSuperAdmin` defaulter til `isAdmin` for bagudkompatibilitet (når kortet
  // bruges af eleven selv), men når en admin/medarbejder impersonerer, sender
  // Dashboard det eksplicit baseret på betragterens faktiske rolle.
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/student/okonomi`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Kunne ikke hente økonomidata.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleProfilUpdate = (updates) => {
    setData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        profil: { ...prev.profil, ...updates },
      };
    });
  };

  const handleUddannelseUpdate = (updates) => {
    setData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        uddannelse_info: { ...prev.uddannelse_info, ...updates },
      };
    });
  };

  const handleAdvarselUpdate = (newAdvarsel, deleteId) => {
    setData(prev => {
      if (!prev) return prev;
      const current = prev.profil?.advarsler || [];
      let updated;
      if (deleteId) {
        updated = current.filter(a => a.id !== deleteId);
      } else if (newAdvarsel) {
        updated = [...current, newAdvarsel];
      } else {
        return prev;
      }
      return {
        ...prev,
        profil: { ...prev.profil, advarsler: updated },
      };
    });
  };

  const handleVippsUpdate = (updates) => {
    setData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        vipps: { ...prev.vipps, ...updates },
      };
    });
  };

  // onUpdate(newEntry) = tilføj ny, onUpdate(null, null, idx, entry) = opdater eksisterende
  const handleKarakterUpdate = (newEntry, deleteEmne, idx, updatedEntry) => {
    setData(prev => {
      if (!prev) return prev;
      const current = [...(prev.karakterer || [])];
      if (idx !== undefined && idx !== null && updatedEntry) {
        current[idx] = updatedEntry;
        return { ...prev, karakterer: current };
      }
      if (newEntry) {
        return { ...prev, karakterer: [...current, newEntry] };
      }
      return prev;
    });
  };

  const handleSamtaleUpdate = (samtaleItemId, newAfholdt) => {
    setData(prev => {
      if (!prev) return prev;
      const updated = (prev.statussamtaler || []).map(s =>
        s.samtale_item_id === samtaleItemId ? { ...s, afholdt: newAfholdt } : s
      );
      return { ...prev, statussamtaler: updated };
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-[#FF2A6D] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Henter økonomidata...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="flex flex-col items-center gap-3 max-w-sm text-center">
          <AlertCircle className="w-8 h-8 text-[#FF2A6D]" />
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const profil = data.profil;

  /* ─── Left column: profile + photo ─── */
  const leftColumn = (
    <div className="space-y-4">
      {profil && (
        <ProfilCard
          profil={profil}
          isAdmin={isAdmin}
          isSuperAdmin={isSuperAdmin}
          adminToken={adminToken}
          appItemId={data.app_item_id}
          onUpdate={handleProfilUpdate}
        />
      )}
    </div>
  );

  /* ─── Right column cards (shared between desktop and mobile) ─── */
  const sharedCards = (
    <>
      {data.uddannelse_info && (
        <UddannelseCard
          info={data.uddannelse_info}
          afdeling={profil?.afdeling}
          isAdmin={isAdmin}
          canEditUgetabel={canEditUgetabel}
          adminToken={adminToken}
          appItemId={data.app_item_id}
          onUpdate={handleUddannelseUpdate}
        />
      )}

      <FritagelserCard
        ydelser={profil?.fritagede_ydelser}
        kundetyper={profil?.fritagede_kundetyper}
      />

      {data.pakke && <PakkeCard pakke={data.pakke} />}

      {!data.pakke && (
        <div className="bg-card border border-border rounded-2xl p-5 text-center">
          <Package className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Ingen uddannelsespakke registreret</p>
        </div>
      )}

      {/* Betalingsforhold / Vipps-abonnement: KUN superadmin */}
      {isSuperAdmin && (
        <VippsAbonnementCard
          token={token}
          vipps={data.vipps}
          pakke={data.pakke}
          onAgreementCreated={handleVippsUpdate}
        />
      )}

      <StatussamtalerCard
        statussamtaler={data.statussamtaler}
        adminToken={adminToken}
        appItemId={data.app_item_id}
        isAdmin={isAdmin}
        onUpdate={handleSamtaleUpdate}
      />

      {/* Stamdata-sektioner nederst */}
      {isAdmin && (
        <AndetCard
          rekruttering={profil?.rekruttering}
          rundvisning={profil?.rundvisning}
          isAdmin={isAdmin}
          adminToken={adminToken}
          appItemId={data.app_item_id}
          onUpdate={handleProfilUpdate}
        />
      )}

      {isAdmin && (
        <AdvarslerCard
          advarsler={profil?.advarsler}
          isAdmin={isAdmin}
          adminToken={adminToken}
          appItemId={data.app_item_id}
          onUpdate={handleAdvarselUpdate}
        />
      )}
    </>
  );

  return (
    <div className="pt-2">
      {/* Desktop: 2-column layout */}
      <div className="hidden md:grid md:grid-cols-[340px_1fr] md:gap-6 md:max-w-4xl md:mx-auto">
        {leftColumn}
        <div className="space-y-4">
          {sharedCards}
        </div>
      </div>

      {/* Mobile: single-column layout */}
      <div className="md:hidden max-w-lg mx-auto space-y-4">
        {profil && (
          <ProfilCard
            profil={profil}
            isAdmin={isAdmin}
            isSuperAdmin={isSuperAdmin}
            adminToken={adminToken}
            appItemId={data.app_item_id}
            onUpdate={handleProfilUpdate}
          />
        )}
        {sharedCards}
      </div>
    </div>
  );
}
